<template>
  <div class="panel-home">
    <div class="home-summary">
      <div class="home-summary-left">
        <div class="home-summary-title">MAZDA 综合维护</div>
        <div class="home-summary-desc">文件、AB、Redis、车型数据、版本与账号集中入口。</div>
      </div>
      <div class="home-summary-right">
        <div class="summary-mini-card">
          <span class="summary-mini-label">当前服务器</span>
          <strong>{{ serverName }}</strong>
        </div>
        <div class="summary-mini-card">
          <span class="summary-mini-label">运行模式</span>
          <strong>{{ serverModeLabel }}</strong>
        </div>
        <div class="summary-mini-card">
          <span class="summary-mini-label">Redis</span>
          <strong>DB1 / mazda</strong>
        </div>
      </div>
    </div>

    <div class="panel-grid dashboard-grid">
      <template v-if="loading">
        <div
          v-for="n in skeletonCount"
          :key="'skel-' + n"
          class="entry-card entry-card-skeleton"
        >
          <div class="entry-main">
            <div class="entry-icon skeleton-pulse"></div>
            <div class="entry-copy">
              <div class="skeleton-line skeleton-line-short skeleton-pulse"></div>
              <div class="skeleton-line skeleton-line-long skeleton-pulse"></div>
              <div class="skeleton-line skeleton-line-mid skeleton-pulse"></div>
            </div>
          </div>
          <div class="entry-meta">
            <div class="skeleton-tag skeleton-pulse"></div>
            <div class="skeleton-tag skeleton-pulse"></div>
          </div>
          <div class="entry-footer skeleton-footer">
            <div class="skeleton-line skeleton-line-short skeleton-pulse"></div>
          </div>
        </div>
      </template>
      <template v-else>
        <el-card
          v-for="item in visibleEntries"
          :key="item.panel"
          class="entry-card"
          :class="item.colorClass"
          shadow="hover"
          @click="emit('open-panel', item.panel)"
        >
          <div class="entry-main">
            <div class="entry-icon">{{ item.icon }}</div>
            <div class="entry-copy">
              <div class="entry-kicker">{{ item.tag }}</div>
              <div class="entry-title">{{ item.title }}</div>
              <div class="entry-desc">{{ item.desc }}</div>
            </div>
          </div>
          <div class="entry-meta">
            <span v-for="meta in item.meta" :key="meta">{{ meta }}</span>
          </div>
          <div class="entry-footer">
            <span>{{ item.footer }}</span>
            <span class="entry-arrow">→</span>
          </div>
        </el-card>
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  serverName: { type: String, required: true },
  serverModeLabel: { type: String, default: '' },
  allowedPanels: { type: Array, default: () => [] },
  limitedAccess: { type: Boolean, default: false },
  loading: { type: Boolean, default: false },
});

const emit = defineEmits(['open-panel']);

const allEntries = [
  {
    panel: 'replace',
    icon: '📦',
    tag: '文件',
    title: '上传文件',
    desc: '替换目标路径文件',
    meta: ['路径校验', '文件名检查'],
    footer: '进入',
    colorClass: 'entry-card-blue',
  },
  {
    panel: 'ab',
    icon: '🧩',
    tag: 'SQL',
    title: 'AB 数据',
    desc: '.ab 写入与列表查看',
    meta: ['VIN', '内容查看'],
    footer: '进入',
    colorClass: 'entry-card-violet',
  },
  {
    panel: 'dataOps',
    icon: '🗃️',
    tag: 'Redis / 数据',
    title: '数据维护',
    desc: '日志、车型数据、测试接头',
    meta: ['DB1', '下载'],
    footer: '进入',
    colorClass: 'entry-card-green',
  },
  {
    panel: 'soVersion',
    icon: '🧬',
    tag: '版本',
    title: 'SO 版本',
    desc: '正式 / 测试 / SO 项',
    meta: ['Vxx.xx', 'TRUE/FALSE'],
    footer: '进入',
    colorClass: 'entry-card-cyan',
  },
  {
    panel: 'accountSettings',
    icon: '🔐',
    tag: '账号',
    title: '账号与 GGP',
    desc: '账号、Cookie、GGP',
    meta: ['Cookie', 'GGP'],
    footer: '进入',
    colorClass: 'entry-card-purple',
  },
  {
    panel: 'interfaceDocs',
    icon: '📋',
    tag: '接口',
    title: '接口说明',
    desc: '功能号、版本启用、弃用状态',
    meta: ['207', '220-233'],
    footer: '进入',
    colorClass: 'entry-card-orange',
  },
  {
    panel: 'crawlerBox',
    icon: '🔌',
    tag: '测试',
    title: '测试接头处理',
    desc: '接头开关状态维护',
    meta: ['CrawlerBox', 'TRUE/FALSE'],
    footer: '进入',
    colorClass: 'entry-card-teal',
  },
  {
    panel: 'sm2Keys',
    icon: 'SM2',
    tag: 'SM2',
    title: 'SM2 密钥管理',
    desc: '公私钥、生成时间、双服务器同步',
    meta: ['5组有效', '12小时清理'],
    footer: '进入',
    colorClass: 'entry-card-indigo',
  },
];

const dataOpsEntry = allEntries.find((item) => item.panel === 'dataOps');
if (dataOpsEntry) {
  dataOpsEntry.desc = '日志、车型数据、Redis 数据维护';
}

const visibleEntries = computed(() => {
  if (!props.allowedPanels.length) {
    return allEntries;
  }

  return props.allowedPanels
    .map((panel) => allEntries.find((item) => item.panel === panel))
    .filter(Boolean);
});

const skeletonCount = computed(() => {
  if (props.allowedPanels.length) {
    return props.allowedPanels.length;
  }
  return allEntries.length;
});
</script>

<style scoped>
.panel-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(220px, 1fr));
  gap: 12px;
}

.dashboard-grid {
  gap: 12px;
}

.home-summary {
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 14px;
  padding: 14px 16px;
  border-radius: 14px;
  border: 1px solid rgba(226, 233, 243, 0.95);
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 8px 18px rgba(26, 46, 79, 0.06);
}

.home-summary-left {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.home-summary-title {
  font-size: 18px;
  font-weight: 800;
  color: #1f2d3d;
}

.home-summary-desc {
  margin-top: 8px;
  color: #667085;
  line-height: 1.55;
  font-size: 13px;
}

.home-summary-right {
  display: flex;
  gap: 12px;
  align-items: stretch;
  flex-wrap: wrap;
}

.summary-mini-card {
  min-width: 128px;
  padding: 10px 12px;
  border-radius: 10px;
  background: linear-gradient(180deg, #f7faff, #f3f7ff);
  border: 1px solid #e8eef8;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 6px;
}

.summary-mini-label {
  color: #8a94a6;
  font-size: 12px;
}

.entry-card {
  position: relative;
  overflow: hidden;
  cursor: pointer;
  min-height: 144px;
  border-radius: 8px;
  border: 1px solid rgba(226, 233, 243, 0.95);
  box-shadow: 0 8px 18px rgba(26, 46, 79, 0.06);
  transition: transform 0.24s ease, box-shadow 0.24s ease, border-color 0.24s ease;
}

.entry-card::before {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  opacity: 0.9;
}

.entry-card-blue::before {
  background: linear-gradient(135deg, rgba(64, 158, 255, 0.14), transparent 56%);
}

.entry-card-violet::before {
  background: linear-gradient(135deg, rgba(114, 95, 255, 0.15), transparent 56%);
}

.entry-card-green::before {
  background: linear-gradient(135deg, rgba(103, 194, 58, 0.14), transparent 56%);
}

.entry-card-orange::before {
  background: linear-gradient(135deg, rgba(230, 162, 60, 0.15), transparent 56%);
}

.entry-card-cyan::before {
  background: linear-gradient(135deg, rgba(64, 190, 190, 0.15), transparent 56%);
}

.entry-card-teal::before {
  background: linear-gradient(135deg, rgba(45, 168, 150, 0.15), transparent 56%);
}

.entry-card-purple::before {
  background: linear-gradient(135deg, rgba(145, 93, 255, 0.15), transparent 56%);
}

.entry-card-indigo::before {
  background: linear-gradient(135deg, rgba(78, 115, 223, 0.15), transparent 56%);
}

.entry-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 14px 26px rgba(30, 56, 93, 0.1);
  border-color: rgba(64, 158, 255, 0.24);
}

.entry-main,
.entry-copy,
.entry-title,
.entry-desc,
.entry-meta,
.entry-footer {
  position: relative;
  z-index: 1;
}

.entry-main {
  display: flex;
  align-items: flex-start;
  gap: 11px;
  margin-bottom: 10px;
}

.entry-icon {
  width: 38px;
  height: 38px;
  flex: 0 0 38px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  background: linear-gradient(135deg, #eef5ff, #f8fbff);
  box-shadow: inset 0 0 0 1px rgba(64, 158, 255, 0.08);
}

.entry-card-indigo .entry-icon {
  font-size: 13px;
  font-weight: 800;
  color: #4e73df;
}

.entry-copy {
  min-width: 0;
}

.entry-kicker {
  color: #6b7383;
  font-size: 11px;
  font-weight: 800;
  line-height: 1.2;
}

.entry-title {
  margin-top: 3px;
  font-size: 16px;
  font-weight: 800;
  color: #1f2d3d;
  line-height: 1.25;
}

.entry-desc {
  margin-top: 4px;
  line-height: 1.45;
  color: #606266;
  font-size: 12px;
  min-height: 0;
}

.entry-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
}

.entry-meta span {
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 8px;
  background: rgba(247, 250, 255, 0.95);
  border: 1px solid rgba(230, 236, 244, 0.9);
  color: #6b7383;
  font-size: 11px;
}

.entry-footer {
  margin-top: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #2f6fd6;
  font-weight: 700;
  font-size: 12px;
}

.entry-arrow {
  font-size: 18px;
}

@media (max-width: 1180px) {
  .panel-grid {
    grid-template-columns: repeat(2, minmax(220px, 1fr));
  }

  .home-summary {
    flex-direction: column;
  }
}

@media (max-width: 680px) {
  .panel-grid {
    grid-template-columns: 1fr;
  }
}

/* Skeleton loading styles */
.entry-card-skeleton {
  cursor: default;
  pointer-events: none;
}

.entry-card-skeleton::before {
  background: none;
}

.skeleton-pulse {
  background: linear-gradient(90deg, #eef1f6 25%, #f7f9fc 50%, #eef1f6 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s ease-in-out infinite;
  border-radius: 6px;
}

@keyframes skeleton-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.skeleton-line {
  height: 14px;
  margin-top: 6px;
}

.skeleton-line-short {
  width: 40%;
}

.skeleton-line-long {
  width: 70%;
}

.skeleton-line-mid {
  width: 55%;
}

.skeleton-tag {
  display: inline-block;
  width: 56px;
  height: 26px;
  border-radius: 8px;
}

.skeleton-footer {
  margin-top: 12px;
}
</style>
